import { Column } from "@ant-design/charts";
import { Button, Card, Col, Empty, message, Row, Select, Space, Spin, Typography } from "antd";
import { GET_SEWING_LINES, SEWING_LINE_DEFECT_REPORT } from "apiServices/API_ENDPOINTS";
import { getData } from "apiServices/common";
import CustomDatePicker from "common/CustomDatePicker";
import Header from "components/Common/Header";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import "./index.css";

const { Title, Text } = Typography;

const CATEGORY_COLORS = ["#4A90D9", "#5BC0BE", "#F5C518", "#8E9AAF", "#7CB342"];

const getDefaultDates = () => ({
  previousDate: moment().subtract(1, "day"),
  currentDate: moment(),
});

const formatDefectRate = (value) => {
  const rate = Number(value);
  return Number.isFinite(rate) ? `${rate.toFixed(2)}%` : "0.00%";
};

const formatPercentage = (value) => {
  const rate = Number(value);
  return Number.isFinite(rate) ? `${rate.toFixed(2)}%` : "0.00%";
};

const getDhuChartYMax = (maxValue) => {
  if (maxValue <= 0) {
    return 3.5;
  }

  return Math.max(3.5, Math.ceil((maxValue + 0.3) * 2) / 2);
};

const getCategoryChartYMax = (maxValue) => {
  if (maxValue <= 0) {
    return 100;
  }

  return Math.min(100, Math.ceil((maxValue + 5) / 10) * 10);
};

const buildCategoryChartConfig = (categories) => {
  const chartData = (categories || []).map((category, index) => {
    const percentage = parseFloat(category.percentage);
    const value = Number.isFinite(percentage) ? percentage : 0;

    return {
      category: category.category_name || `Category ${index + 1}`,
      value,
      label: formatPercentage(value),
    };
  });

  const maxValue = Math.max(...chartData.map((item) => item.value), 0);
  const yMax = getCategoryChartYMax(maxValue);

  return {
    data: chartData,
    xField: "category",
    yField: "value",
    height: 260,
    autoFit: true,
    insetTop: 24,
    insetLeft: 8,
    insetRight: 8,
    legend: false,
    scale: {
      y: {
        domainMin: 0,
        domainMax: yMax,
      },
    },
    style: {
      fill: (datum) => {
        const index = chartData.findIndex((item) => item.category === datum.category);
        return CATEGORY_COLORS[index] ?? CATEGORY_COLORS[0];
      },
      stroke: (datum) => {
        const index = chartData.findIndex((item) => item.category === datum.category);
        return CATEGORY_COLORS[index] ?? CATEGORY_COLORS[0];
      },
      lineWidth: 1,
    },
    axis: {
      x: {
        title: false,
        labelFill: "#1a2b49",
        labelFontWeight: 600,
        labelFontSize: 11,
        labelAutoRotate: true,
      },
      y: {
        title: false,
        labelFontSize: 10,
        labelFormatter: (value) => formatPercentage(value),
        grid: true,
        gridStroke: "#e8e8e8",
        gridLineWidth: 1,
      },
    },
    label: {
      text: (datum) => datum.label,
      position: "top",
      style: {
        fill: "#1a2b49",
        fontSize: 11,
        fontWeight: 600,
        dy: -6,
        textAlign: "center",
        textBaseline: "bottom",
      },
    },
    tooltip: {
      title: "category",
      items: [
        {
          channel: "y",
          name: "Percentage",
          valueFormatter: (value) => formatPercentage(value),
        },
      ],
    },
  };
};

export default function SewingLineDefect() {
  const defaultDates = useMemo(() => getDefaultDates(), []);
  const [previousDate, setPreviousDate] = useState(defaultDates.previousDate);
  const [currentDate, setCurrentDate] = useState(defaultDates.currentDate);
  const [lineId, setLineId] = useState(undefined);
  const [sewingLines, setSewingLines] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const fetchSewingLines = useCallback(async () => {
    const response = await getData(GET_SEWING_LINES);

    if (response?.data?.code === 200) {
      setSewingLines(response?.data?.data || []);
    }
  }, []);

  const fetchReport = useCallback(async (selectedPreviousDate, selectedCurrentDate, selectedLineId) => {
    if (!selectedPreviousDate || !selectedCurrentDate) {
      return;
    }

    setLoading(true);
    setHasSearched(true);

    try {
      const params = {
        from_date: selectedPreviousDate.format("YYYY-MM-DD"),
        to_date: selectedCurrentDate.format("YYYY-MM-DD"),
      };

      if (selectedLineId) {
        params.line_id = selectedLineId;
      }

      const response = await getData(SEWING_LINE_DEFECT_REPORT, false, params);

      if (response?.data?.code === 200) {
        setReportData(response?.data?.data || null);
      } else {
        setReportData(null);
      }
    } catch (error) {
      console.error(error);
      setReportData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSewingLines();
  }, [fetchSewingLines]);

  useEffect(() => {
    fetchReport(defaultDates.previousDate, defaultDates.currentDate, undefined);
  }, [defaultDates, fetchReport]);

  const searchedLineName = useMemo(() => {
    const searchedLineId = reportData?.filters?.line_id;

    if (!searchedLineId) {
      return "All Lines";
    }

    return sewingLines.find((line) => line.id === searchedLineId)?.name || `Line ${searchedLineId}`;
  }, [reportData, sewingLines]);

  const comparisonChartData = useMemo(() => {
    return (reportData?.bar_chart || []).map((item) => {
      const rate = parseFloat(item.defect_rate);
      const value = Number.isFinite(rate) ? rate : 0;

      return {
        date: item.date_display || item.date_label,
        value,
        label: formatDefectRate(value),
      };
    });
  }, [reportData]);

  const dateWiseCategories = reportData?.date_wise_defect_categories || [];
  const previousDateSection = dateWiseCategories.find(
    (section) => section.date_type_label === "Previous Date",
  );
  const currentDateSection = dateWiseCategories.find(
    (section) => section.date_type_label === "Current Date",
  );

  const comparisonChartConfig = useMemo(() => {
    const maxValue = Math.max(...comparisonChartData.map((item) => item.value), 0);
    const yMax = getDhuChartYMax(maxValue);

    return {
      data: comparisonChartData,
      xField: "date",
      yField: "value",
      height: 280,
      autoFit: true,
      insetTop: 24,
      insetLeft: 8,
      insetRight: 8,
      legend: false,
      scale: {
        y: {
          domainMin: 0,
          domainMax: yMax,
          tickInterval: 0.5,
        },
      },
      style: {
        fill: "l(270) 0:#d8f2fa 0.5:#a8e0f0 1:#7ec8e8",
        stroke: "#3d9cb5",
        lineWidth: 1.5,
      },
      axis: {
        x: {
          title: false,
          labelFill: "#1a2b49",
          labelFontWeight: 600,
          labelFontSize: 11,
        },
        y: {
          title: false,
          labelFontSize: 10,
          labelFormatter: (value) => formatDefectRate(value),
          grid: true,
          gridStroke: "#e8e8e8",
          gridLineWidth: 1,
        },
      },
      label: {
        text: (datum) => datum.label,
        position: "top",
        style: {
          fill: "#1a2b49",
          fontSize: 11,
          fontWeight: 600,
          dy: -6,
          textAlign: "center",
          textBaseline: "bottom",
        },
      },
      tooltip: {
        title: "date",
        items: [
          {
            channel: "y",
            name: "Defect Rate",
            valueFormatter: (value) => formatDefectRate(value),
          },
        ],
      },
    };
  }, [comparisonChartData]);

  const handleSearch = () => {
    if (!previousDate || !currentDate) {
      message.warning("Please select both Previous Date and Current Date");
      return;
    }

    if (previousDate.isAfter(currentDate)) {
      message.warning("Previous Date cannot be greater than Current Date");
      return;
    }

    fetchReport(previousDate, currentDate, lineId);
  };

  const handleReset = () => {
    const dates = getDefaultDates();
    setPreviousDate(dates.previousDate);
    setCurrentDate(dates.currentDate);
    setLineId(undefined);
    fetchReport(dates.previousDate, dates.currentDate, undefined);
  };

  const renderCategoryItem = (category, index) => (
    <div className="sewing-line-defect-category" key={category.category_id || index}>
      <div
        className="sewing-line-defect-category__badge"
        style={{ backgroundColor: CATEGORY_COLORS[index] }}
      >
        {String(index + 1).padStart(2, "0")}
      </div>
      <div className="sewing-line-defect-category__content">
        <span className="sewing-line-defect-category__name">{category.category_name}</span>
        <span className="sewing-line-defect-category__percentage">
          {Number(category.percentage).toFixed(2)}%
        </span>
      </div>
    </div>
  );

  const renderDateCategoryPanel = (dateSection) => {
    if (!dateSection) {
      return (
        <div className="sewing-line-defect-empty">
          <Empty description="No defect category data found for this date" />
        </div>
      );
    }

    const categories = dateSection.categories || [];
    const chartConfig = buildCategoryChartConfig(categories);

    return (
      <>
        <div className="sewing-line-defect-report__header sewing-line-defect-report__header--panel">
          {/* <Title level={5} className="sewing-line-defect-report__title">
            {dateSection.date_type_label}
          </Title> */}
          <Text className="sewing-line-defect-report__subtitle">
            {dateSection.date_label} · Top 5 Defect Categories
          </Text>
        </div>

        <div className="sewing-line-defect-report__chart sewing-line-defect-report__chart--panel">
          {categories.length > 0 ? (
            <Column
              key={`${dateSection.date}-${categories.map((item) => item.category_id).join("-")}`}
              {...chartConfig}
            />
          ) : (
            <div className="sewing-line-defect-empty">
              <Empty description="No defect category data found for this date" />
            </div>
          )}
        </div>

        {categories.length > 0 && (
          <div className="sewing-line-defect-report__categories sewing-line-defect-report__categories--panel">
            {categories.map((category, index) => renderCategoryItem(category, index))}
          </div>
        )}
      </>
    );
  };

  return (
    <div className="sewing-line-defect-page">
      <Header title="Sewing Line Defect" />

      <Card bordered={false} className="sewing-line-defect-filters" bodyStyle={{ padding: "12px 16px" }}>
        <Row gutter={[12, 8]} align="bottom">
          <Col xs={24} sm={12} md={6} lg={5} xl={4}>
            <label className="sewing-line-defect-filters__label">
              Previous Date <span style={{ color: "red" }}>*</span>
            </label>
            <CustomDatePicker
              allowClear
              placeholder="Previous Date"
              style={{ width: "100%" }}
              value={previousDate}
              onChange={(date) => setPreviousDate(date)}
            />
          </Col>
          <Col xs={24} sm={12} md={6} lg={5} xl={4}>
            <label className="sewing-line-defect-filters__label">
              Current Date <span style={{ color: "red" }}>*</span>
            </label>
            <CustomDatePicker
              allowClear
              placeholder="Current Date"
              style={{ width: "100%" }}
              value={currentDate}
              onChange={(date) => setCurrentDate(date)}
            />
          </Col>
          <Col xs={24} sm={12} md={6} lg={5} xl={4}>
            <label className="sewing-line-defect-filters__label">Line</label>
            <Select
              allowClear
              showSearch
              size="small"
              placeholder="All Lines"
              optionFilterProp="children"
              style={{ width: "100%" }}
              value={lineId}
              onChange={(value) => setLineId(value)}
            >
              {sewingLines.map((line) => (
                <Select.Option key={line.id} value={line.id}>
                  {line.name}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6} lg={9} xl={6}>
            <div className="sewing-line-defect-filters__actions">
              <Space size={8}>
                <Button
                  type="primary"
                  size="small"
                  onClick={handleSearch}
                  loading={loading}
                  disabled={!previousDate || !currentDate || loading}
                >
                  Search
                </Button>
                <Button size="small" onClick={handleReset} disabled={loading}>
                  Reset
                </Button>
              </Space>
            </div>
          </Col>
        </Row>
      </Card>

      <Spin spinning={loading}>
        <div className="sewing-line-defect-report">
          {!hasSearched ? (
            <div className="sewing-line-defect-empty">
              <Empty description="Select dates and click Search to view report" />
            </div>
          ) : (
            <>
              <div className="sewing-line-defect-report__comparison">
                <div className="sewing-line-defect-report__header">
                  <Title level={5} className="sewing-line-defect-report__title">
                    Sewing Line - {searchedLineName}
                  </Title>
                  <Text className="sewing-line-defect-report__subtitle">DHU</Text>
                </div>

                <div className="sewing-line-defect-report__chart">
                  {comparisonChartData.length > 0 ? (
                    <Column
                      key={comparisonChartData.map((item) => `${item.date}-${item.value}`).join("-")}
                      {...comparisonChartConfig}
                    />
                  ) : (
                    <div className="sewing-line-defect-empty">
                      <Empty description="No comparison data found for the selected filters" />
                    </div>
                  )}
                </div>
              </div>

              <div className="sewing-line-defect-report__category-section">
                <Row gutter={0}>
                  <Col xs={24} lg={12} className="sewing-line-defect-report__date-panel">
                    {renderDateCategoryPanel(previousDateSection)}
                  </Col>
                  <Col xs={24} lg={12} className="sewing-line-defect-report__date-panel">
                    {renderDateCategoryPanel(currentDateSection)}
                  </Col>
                </Row>
              </div>
            </>
          )}
        </div>
      </Spin>
    </div>
  );
}
